import { CHARSET_PRINTABLE_ASCII, GLYPH_HEIGHT } from "./constants.js";
/**
 * Approximate MapleStory's quantized grayscale result when two regular-font
 * black-ink contributions overlap on white.
 *
 * The fit is empirical: weak overlaps use a moderated contribution from the
 * lighter pixel, then transition continuously to alpha union for stronger
 * pixels. MapleStory's observed regular-font grayscale palette uses 16 levels,
 * so nontrivial overlaps are quantized to 1/15 steps.
 *
 * See docs/glyph_weight_composition.md and the reproducible captures under
 * data/regular-glyph-atlas/overlap-composition/.
 */
export function blendGlyphWeights(a, b) {
    const left = Math.min(1, Math.max(0, a));
    const right = Math.min(1, Math.max(0, b));
    if (left === 0)
        return right;
    if (right === 0)
        return left;
    if (left === 1 || right === 1)
        return 1;
    const high = Math.max(left, right);
    const low = Math.min(left, right);
    const weakGain = (1 + high) / 2;
    const alphaUnionGain = 1 - high;
    const raw = high + low * Math.min(weakGain, alphaUnionGain);
    return Math.round(raw * 15) / 15;
}
export function glyphWeightAt(glyph, x, y) {
    if (x < 0 || x >= glyph.visualWidth || y < 0 || y >= GLYPH_HEIGHT)
        return 0;
    return glyph.weights[y * glyph.visualWidth + x] ?? 0;
}
export function glyphRightOverhangWidth(glyph) {
    return glyph.visualWidth - glyph.width;
}
/** Render a glyph sequence using logical advances and optional right overhang. */
export function composeGlyphSequenceWeights(glyphs) {
    let logicalWidth = 0;
    let visualWidth = 0;
    for (const glyph of glyphs) {
        visualWidth = Math.max(visualWidth, logicalWidth + glyph.visualWidth);
        logicalWidth += glyph.width;
    }
    visualWidth = Math.max(visualWidth, logicalWidth);
    const weights = new Float32Array(visualWidth * GLYPH_HEIGHT);
    let cursor = 0;
    for (const glyph of glyphs) {
        for (let y = 0; y < GLYPH_HEIGHT; y++) {
            for (let dx = 0; dx < glyph.visualWidth; dx++) {
                const value = glyphWeightAt(glyph, dx, y) / 255;
                if (value <= 0)
                    continue;
                const x = cursor + dx;
                if (x < 0 || x >= visualWidth)
                    continue;
                const index = y * visualWidth + x;
                weights[index] = blendGlyphWeights(weights[index] ?? 0, value);
            }
        }
        cursor += glyph.width;
    }
    return { logicalWidth, visualWidth, weights };
}
/** Compose glyph binary masks using logical advances and Boolean OR overlap. */
export function composeGlyphSequenceBinaryColumns(glyphs) {
    let logicalWidth = 0;
    let visualWidth = 0;
    for (const glyph of glyphs) {
        visualWidth = Math.max(visualWidth, logicalWidth + glyph.visualWidth);
        logicalWidth += glyph.width;
    }
    visualWidth = Math.max(visualWidth, logicalWidth);
    const columns = new Uint16Array(visualWidth);
    let cursor = 0;
    for (const glyph of glyphs) {
        for (let dx = 0; dx < glyph.visualWidth; dx++) {
            columns[cursor + dx] = (columns[cursor + dx] ?? 0) | (glyph.binaryColumns[dx] ?? 0);
        }
        cursor += glyph.width;
    }
    return { logicalWidth, visualWidth, columns };
}
/** Decode a base64 string into a Uint8Array.
 *
 * The generated atlas stores weighted templates in base64 to keep the generated
 * TypeScript file small. Node uses Buffer; browser-like environments can use
 * atob if the decoder is reused outside the CLI.
 */
export function decodeBase64Bytes(value) {
    const bufferCtor = globalThis.Buffer;
    if (bufferCtor) {
        return new Uint8Array(bufferCtor.from(value, "base64"));
    }
    const atobFn = globalThis.atob;
    if (!atobFn)
        throw new Error("No base64 decoder is available in this environment");
    const binary = atobFn(value);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i);
    }
    return out;
}
/** Build runtime lookup maps from generated raw glyph specs.
 *
 * Exact-anchor candidate generation uses width-keyed string lookup:
 *
 *   lookupByWidth.get(width).get(inputAnchorString.substring(x, x + width))
 *
 * Each anchor string contains one 12-bit column mask per UTF-16 code unit.
 */
export function buildGlyphAtlas(rawGlyphs, options = {}) {
    const charset = options.charset ?? CHARSET_PRINTABLE_ASCII;
    const rawByChar = new Map();
    for (const raw of rawGlyphs)
        rawByChar.set(raw.char, raw);
    const order = new Map();
    const selectedRaw = [];
    for (let i = 0; i < charset.length; i++) {
        const ch = charset.charAt(i);
        if (order.has(ch))
            continue;
        const raw = rawByChar.get(ch);
        if (!raw)
            throw new Error(`charset contains unavailable glyph: ${JSON.stringify(ch)}`);
        order.set(ch, selectedRaw.length);
        selectedRaw.push(raw);
    }
    const glyphs = selectedRaw.map((raw, fallbackIndex) => {
        if (raw.anchorKey.length !== raw.width) {
            throw new Error(`glyph ${JSON.stringify(raw.char)} anchor width ${raw.anchorKey.length} differs from logical width ${raw.width}`);
        }
        const weights = decodeBase64Bytes(raw.weightsB64);
        if (weights.length % GLYPH_HEIGHT !== 0) {
            throw new Error(`glyph ${JSON.stringify(raw.char)} has invalid weight length: ${weights.length}`);
        }
        const visualWidth = weights.length / GLYPH_HEIGHT;
        if (visualWidth < raw.width) {
            throw new Error(`glyph ${JSON.stringify(raw.char)} visual width ${visualWidth} is less than logical width ${raw.width}`);
        }
        return {
            char: raw.char,
            width: raw.width,
            visualWidth,
            anchorKey: raw.anchorKey,
            weights,
            binaryColumns: binaryColumnsFromWeights(weights, visualWidth),
            orderIndex: order.get(raw.char) ?? fallbackIndex,
        };
    });
    return buildLookupAtlas(glyphs);
}
function binaryColumnsFromWeights(weights, visualWidth) {
    const columns = new Uint16Array(visualWidth);
    for (let y = 0; y < GLYPH_HEIGHT; y++) {
        const row = y * visualWidth;
        for (let x = 0; x < visualWidth; x++) {
            if ((weights[row + x] ?? 0) !== 0)
                columns[x] = (columns[x] ?? 0) | (1 << y);
        }
    }
    return columns;
}
export function filterGlyphAtlas(atlas, charset) {
    const order = new Map();
    const selectedGlyphs = [];
    for (let i = 0; i < charset.length; i++) {
        const ch = charset.charAt(i);
        if (order.has(ch))
            continue;
        const glyph = atlas.byChar.get(ch);
        if (!glyph)
            throw new Error(`charset contains unavailable glyph: ${JSON.stringify(ch)}`);
        order.set(ch, selectedGlyphs.length);
        selectedGlyphs.push({
            ...glyph,
            orderIndex: selectedGlyphs.length,
        });
    }
    return buildLookupAtlas(selectedGlyphs);
}
function buildLookupAtlas(glyphs) {
    glyphs.sort((a, b) => a.orderIndex - b.orderIndex);
    const byChar = new Map();
    const lookupByWidth = new Map();
    for (const glyph of glyphs) {
        byChar.set(glyph.char, glyph);
        let table = lookupByWidth.get(glyph.width);
        if (!table) {
            table = new Map();
            lookupByWidth.set(glyph.width, table);
        }
        const existing = table.get(glyph.anchorKey);
        if (existing)
            existing.push(glyph);
        else
            table.set(glyph.anchorKey, [glyph]);
    }
    for (const table of lookupByWidth.values()) {
        for (const list of table.values()) {
            list.sort((a, b) => a.orderIndex - b.orderIndex);
        }
    }
    const widths = [...lookupByWidth.keys()].sort((a, b) => a - b);
    return { glyphs, widths, byChar, lookupByWidth };
}
//# sourceMappingURL=atlas.js.map